import { Box, CircularProgress, FormControlLabel, Switch, Tooltip } from "@mui/material";
import { DataGrid } from "../../../components/datagrid/DataGrid";
import { Suspense, useMemo, useState } from "react";
import { SPECIAL_USERS, ROLES } from "../../../utils/constants";
import Calendar from "../../../components/calendar/Calendar";
import { ToggleBetweenCalendarAndList } from "./ToggleBetweenCalendarAndList";
import { useRecordsContext } from "../../../utils/useRecordsContext";
import { useUser } from "../../../api/useAuth";
import { UseRecordsOptions } from "../../../api/useRecords";
import { removeRedundantCancelledSessions } from "../utils";
import { RelatedRecordsContext } from "../../../components/recordsRelated/RecordsRelated";

type RelatedSessionsProps = {
    heightOfHeaders: number;
    sessionsOptions: UseRecordsOptions;
};

export const SessionsRelated = ({ heightOfHeaders, sessionsOptions }: RelatedSessionsProps) => {
    const context = useRecordsContext("relatedRecords");
    const { entity, items, paginationModel, refetch } = context;

    const [hideCancelledByStaff, setHideCancelledByStaff] = useState(true);

    const filteredSessions = useMemo(() => {
        const base = items ?? [];
        return hideCancelledByStaff
            ? base.filter((session) => session.status !== "staff_unavailable")
            : base;
    }, [items, hideCancelledByStaff]);

    const calendarSessions = useMemo(
        () => removeRedundantCancelledSessions(filteredSessions),
        [filteredSessions]
    );

    const filteredContext = useMemo(
        () => ({ ...context, items: filteredSessions }),
        [context, filteredSessions]
    );

    const { data: user } = useUser();
    const isRecepcionistOrJoana =
        user?.role.id === ROLES.recepcionist ||
        user?.role.id === ROLES.substituion_joana_temp ||
        user?.id === SPECIAL_USERS.joana_director;

    const [calendarView, setCalendarView] = useState<"calendar" | "list">(
        entity === "sessions" && !isRecepcionistOrJoana ? "calendar" : "list"
    );

    return (
        <RelatedRecordsContext.Provider value={filteredContext}>
            <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: { xs: "center", sm: "flex-start" }, mr: { xs: 0, sm: 4 }, mt: { xs: 0, sm: -16 }, mb: 4, gap: 2 }}>
                    <ToggleBetweenCalendarAndList setView={setCalendarView} style={{}} />
                    <Tooltip title="Esconder sessões canceladas por indisponibilidade">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={hideCancelledByStaff}
                                    onChange={(e) => setHideCancelledByStaff(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Esconder canceladas pela clínica"
                            sx={{ ml: 1 }}
                        />
                    </Tooltip>
                </Box>
                {calendarView === "calendar" && (
                    <Suspense fallback={<CircularProgress />}>
                        {refetch && (
                            <Calendar
                                items={calendarSessions}
                                isMobile={false}
                                refetch={refetch}
                                views={{ day: false, agenda: false, week: false, month: false, year: true }}
                                defaultView="year"
                                height={1800}
                            />
                        )}
                    </Suspense>
                )}
                {calendarView === "list" && (
                    <DataGrid paginationModel={paginationModel} heightOfHeaders={heightOfHeaders} type="relatedRecords" options={sessionsOptions.dataGridOptions} />
                )}
            </Box>
        </RelatedRecordsContext.Provider>
    );
};